import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  RadialBarChart, RadialBar
} from 'recharts';
import { FiFilter, FiDownload, FiCalendar, FiBarChart2 } from 'react-icons/fi';

const C = { green: '#7ea52e', red: '#1a1a18', sky: '#7ea52e', purple: '#1a1a18', orange: '#9ec25a' };

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(126,165,46,0.12)', borderRadius: 14,
      padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    }}>
      <p style={{ color: '#0f172a', fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'Poppins, sans-serif' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill }} />
          <span style={{ color: '#475569', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    {icon && <div style={{ width: 32, height: 32, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sky, fontSize: 15 }}>{icon}</div>}
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 11, color: '#abab9f', margin: '2px 0 0', fontWeight: 500 }}>{subtitle}</p>}
    </div>
  </div>
);

export default function AdminReports() {
  const [report, setReport] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', campaignId: '', status: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/campaigns?limit=100').then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.campaignId) params.campaignId = filters.campaignId;
      if (filters.status) params.status = filters.status;
      const { data } = await api.get('/admin/reports', { params });
      setReport(data);
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const summary = report?.summary || {};
  const totalSent = summary.totalSent || 0;
  const totalFailed = summary.totalFailed || 0;
  const total = summary.total || 0;
  const successRate = summary.successRate || 0;
  const failureRate = summary.failureRate || 0;

  const barData = report ? [
    { name: 'Sent', value: totalSent, fill: C.green },
    { name: 'Failed', value: totalFailed, fill: C.red },
  ] : [];

  const pieData = report ? [
    { name: 'Delivered', value: totalSent, color: C.green },
    { name: 'Failed', value: totalFailed, color: C.red },
  ].filter(d => d.value > 0) : [];
  if (report && pieData.length === 0) pieData.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
  const pieTotal = pieData.reduce((a, c) => a + c.value, 0);

  const gaugeVal = parseFloat(successRate) || 0;
  const gaugeData = [{ name: 'Success', value: gaugeVal, fill: gaugeVal >= 90 ? C.green : gaugeVal >= 70 ? C.orange : C.red }];

  const exportCSV = () => {
    if (!report) return;
    const logs = report.logs || [];
    const rows = [['Time', 'Campaign', 'Status', 'Phone']];
    logs.forEach(l => rows.push([new Date(l.createdAt).toLocaleString(), l.campaignId?.name || '', l.status || '', l.phone || '']));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `report_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported!');
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Campaign analytics & delivery reports</p>
        </div>
        {report && (
          <button className="btn btn-outline" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiDownload /> Export CSV
          </button>
        )}
      </div>

      {/* ── Filters Card ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <SectionHeader icon={<FiFilter />} title="Filters" subtitle="Select date range and campaign" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label"><FiCalendar style={{ marginRight: 4, verticalAlign: 'middle' }} />Start Date</label>
            <input type="date" className="form-input" value={filters.startDate}
              onChange={e => setFilters({ ...filters, startDate: e.target.value })} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label"><FiCalendar style={{ marginRight: 4, verticalAlign: 'middle' }} />End Date</label>
            <input type="date" className="form-input" value={filters.endDate}
              onChange={e => setFilters({ ...filters, endDate: e.target.value })} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Campaign</label>
            <select className="form-select" value={filters.campaignId}
              onChange={e => setFilters({ ...filters, campaignId: e.target.value })}>
              <option value="">All Campaigns</option>
              {campaigns.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={fetchReport} disabled={loading}
            style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FiBarChart2 /> {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* ── Empty State ── */}
      {!report && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '60px 32px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <h3 style={{ color: '#0f172a', fontWeight: 700, marginBottom: 8, fontFamily: 'Poppins' }}>Generate Your Report</h3>
          <p style={{ color: '#7e7e71', fontSize: 14 }}>Select filters above and click "Generate Report" to view analytics</p>
        </div>
      )}

      {report && (
        <>
          {/* ── Stat Cards ── */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-card-icon purple">◫</div>
              <div className="stat-card-value">{total.toLocaleString()}</div>
              <div className="stat-card-label">Total Messages</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-icon green">✓</div>
              <div className="stat-card-value">{totalSent.toLocaleString()}</div>
              <div className="stat-card-label">Sent ({successRate}%)</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-icon red">✕</div>
              <div className="stat-card-value">{totalFailed.toLocaleString()}</div>
              <div className="stat-card-label">Failed ({failureRate}%)</div>
            </div>
          </div>

          {/* ── Charts Row: Bar + Donut + Gauge ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.5fr', gap: 20, marginBottom: 20 }}>
            {/* Bar Chart */}
            <div className="chart-container">
              <SectionHeader icon={<FiBarChart2 />} title="Delivery Results" subtitle="Sent vs Failed breakdown" />
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={barData} barSize={48} margin={{ top: 5, right: 20, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="rptBarG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9ec25a" /><stop offset="100%" stopColor="#7ea52e" />
                    </linearGradient>
                    <linearGradient id="rptBarR" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#333330" /><stop offset="100%" stopColor="#1a1a18" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f4f4f3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#abab9f', fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#abab9f', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<GlassTooltip />} />
                  <Bar dataKey="value" radius={[10, 10, 0, 0]} animationDuration={1200}>
                    {barData.map((d, i) => (
                      <Cell key={i} fill={d.fill === C.green ? 'url(#rptBarG)' : 'url(#rptBarR)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Donut */}
            <div className="chart-container">
              <SectionHeader title="Status Split" subtitle="Delivery composition" />
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={3} dataKey="value" stroke="none" cornerRadius={4}>
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<GlassTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {pieTotal > 0 && (
                <div style={{ textAlign: 'center', marginTop: -24, position: 'relative', zIndex: 2 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', fontFamily: 'Poppins' }}>{pieTotal.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: '#abab9f', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Total</div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 8 }}>
                {pieData.filter(d => d.name !== 'No Data').map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{d.name}: {d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gauge */}
            <div className="chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <SectionHeader title="Success" />
              <ResponsiveContainer width="100%" height={150}>
                <RadialBarChart cx="50%" cy="55%" innerRadius="55%" outerRadius="90%" startAngle={180} endAngle={0} data={gaugeData} barSize={12}>
                  <RadialBar background={{ fill: '#f4f4f3' }} dataKey="value" cornerRadius={8} animationDuration={1500} />
                  <Tooltip content={<GlassTooltip />} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div style={{ textAlign: 'center', marginTop: -16 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: gaugeData[0].fill, fontFamily: 'Poppins' }}>{successRate}%</div>
                <div style={{ fontSize: 10, color: '#abab9f', fontWeight: 600 }}>DELIVERY</div>
              </div>
            </div>
          </div>

          {/* ── Logs Table ── */}
          <div className="card">
            <SectionHeader title="Recent Logs" subtitle="Latest delivery records" />
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Time</th><th>Campaign</th><th>Status</th></tr></thead>
                <tbody>
                  {(report.logs || []).length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: 'center', padding: 32, color: '#abab9f' }}>No logs found</td></tr>
                  ) : (report.logs || []).slice(0, 10).map((log, i) => (
                    <tr key={i}>
                      <td>{new Date(log.createdAt).toLocaleString()}</td>
                      <td>{log.campaignId?.name || '—'}</td>
                      <td><span className={`badge badge-${log.status}`}>{log.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
